import React from 'react';
import invariant from 'tiny-invariant';
import { Box } from 'react-polymorphic-box';
import { isAnchorOnly, extractAnchor, isInternal } from '@walltowall/helpers';
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var defaultElement = 'div';
/**
 * `<View>` sized with a specified aspect ratio.
 *
 * @example
 * With a 16:9 ratio:
 * <AspectRatio x={16} y={9}>Content</AspectRatio>
 */

var AspectRatio = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var x = _ref.x,
    y = _ref.y,
    children = _ref.children,
    props = _objectWithoutPropertiesLoose(_ref, ["ref", "x", "y", "children"]);
  !(x > 0) ? process.env.NODE_ENV !== "production" ? invariant(false, 'Prop x must be a number greater than 0.') : invariant(false) : void 0;
  !(y >= 0) ? process.env.NODE_ENV !== "production" ? invariant(false, 'Prop y must be a number greater than or equal to 0.') : invariant(false) : void 0;
  return React.createElement(Box, Object.assign({
    ref: innerRef,
    as: defaultElement,
    "data-system-aspect-ratio": ""
  }, props), React.createElement("div", {
    "data-system-aspect-ratio-spacer": "",
    "aria-hidden": true,
    style: {
      paddingTop: y / x * 100 + "%"
    }
  }), React.createElement("div", {
    "data-system-aspect-ratio-inner": ""
  }, children));
}); // @ts-ignore: Component type does not currently include displayName

AspectRatio.displayName = 'AspectRatio';
var defaultElement$1 = 'div';
/**
 * `<View>` with a horizontally centered nested `<div>` with an optional
 * maximum width. Gutter width should be added directly to `<BoundedView>` as
 * padding.
 *
 * @example
 * With no inner maximum width:
 * <BoundedView>Content</BoundedView>
 *
 * @example
 * With inner maximum width of `48rem`:
 * <BoundedView maxWidth="48rem">Content</BoundedView>
 */

var BoundedView = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var maxWidth = _ref.maxWidth,
    innerProps = _ref.innerProps,
    children = _ref.children,
    props = _objectWithoutPropertiesLoose(_ref, ["ref", "maxWidth", "innerProps", "children"]);
  return React.createElement(Box, Object.assign({
    ref: innerRef,
    as: defaultElement$1
  }, props), React.createElement("div", Object.assign({
    "data-system-bounded-view-inner": "",
    style: {
      maxWidth: maxWidth
    }
  }, innerProps), children));
}); // @ts-ignore: Component type does not currently include displayName

BoundedView.displayName = 'BoundedView';
var defaultElement$2 = 'div';
/**
 * `<View>` used to wrap a `<GatsbyImage>` component when the `<GatsbyImage>`
 * component is styled to be 100% of its parent's height. This component
 * applies style overrides to the `GatsbyImage`-specific class names.
 *
 * @example
 * Basic usage:
 * <GatsbyImageContainer>
 *   <GatsbyImage fluid={fluid} css={{ height:'100%' }} />
 * </GatsbyImageContainer>
 */

var GatsbyImageContainer = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var props = _objectWithoutPropertiesLoose(_ref, ["ref"]);
  return React.createElement(Box, Object.assign({
    ref: innerRef,
    as: defaultElement$2,
    "data-system-gatsby-image-container": ""
  }, props));
}); // @ts-ignore: Component type does not currently include displayName

GatsbyImageContainer.displayName = 'GatsbyImageContainer';
var defaultElement$3 = 'a';
/**
 * `<View>` that dynamically changes from an `<a>` to a router-specific
 * component depending on if `href` is external or internal.
 *
 * If `href` is external, `target` is automatically set to `_target` and `rel`
 * is automatically set to `nofollow noopener noreferrer`. Both can be
 * overridden by passing their respective props.
 *
 * This component should be used a base for a project-specific Link component
 * that adds a default `routerLinkComponent`.
 *
 * @example
 * With an internal `href`:
 * <Link href="/example">Example</Link>
 *
 * @example
 * With an external `href` rendered with Gatsby's Link:
 * <Link href="https://example.com" routerLinkComponent={GatsbyLink}>
 *   Example
 * </Link>
 */

var Link = /*#__PURE__*/React.forwardRef(function (_ref, innerRef) {
  var _extractAnchor;
  var rawHref = _ref.href,
    _ref$routerLinkCompon = _ref.routerLinkComponent,
    RouterLinkComponent = _ref$routerLinkCompon === void 0 ? 'a' : _ref$routerLinkCompon,
    props = _objectWithoutPropertiesLoose(_ref, ["ref", "href", "routerLinkComponent"]);
  var href = isAnchorOnly(rawHref) ? (_extractAnchor = extractAnchor(rawHref)) !== null && _extractAnchor !== void 0 ? _extractAnchor : '' : rawHref;
  var isInternalHref = isInternal(href); // E.g. tel: or mailto:

  var isSpecialLink = !isInternalHref && !href.startsWith('http');
  return React.createElement(Box, Object.assign({
    ref: innerRef,
    as: isInternalHref ? RouterLinkComponent : defaultElement$3,
    href: href,
    target: isInternalHref || isSpecialLink ? undefined : '_blank',
    rel: isInternalHref || isSpecialLink ? undefined : 'nofollow noopener noreferrer'
  }, props));
}); // @ts-ignore: Component type does not currently include displayName

Link.displayName = 'Link';
export { AspectRatio, BoundedView, GatsbyImageContainer, Link };